import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import useActivation from './hooks/useActivation';
import PhotoManagerList from './PhotoManagerList';
import useShare from '../../Share/components/hooks/useShare';
import BulkUploadMedia from '../../Common/components/BulkUploadMedia';
import AddSessionShareModal from './AddSessionShareModal';
import useGetGalleries from '@/src/GalleryConfiguration/components/hooks/useGetGalleries';
import { Loader2, UploadIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/src/shadComponents/ui/button';
import { Switch } from '@/src/shadComponents/ui/switch';
import { Label } from '@/src/shadComponents/ui/label';

export default function SessionCreate() {
    const [showHidden, setShowHidden] = useState(false);
    const { activationId } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [isSending, setIsSending] = useState<boolean | null>(null);
    const [isShowingBulkUpload, setIsShowingBulkUpload] = useState(false);
    const [isShowingAddShare, setIsShowingAddShare] = useState(false);
    const [photos, setPhotos] = useState<MediaInstance[]>([]);

    const { getActivation, getSessionPhotos } = useActivation();
    const { getGalleriesForActivation } = useGetGalleries();
    const { sendUnsentSharesForSession } = useShare();
    const [uploadCount, setUploadCount] = useState<number>(0);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [selectedShareIdentifiers, setSelectedShareIdentifiers] = useState<
        string[]
    >([]);

    const [photoSessionId] = useState<string>(crypto.randomUUID());

    const [galleryId, setGalleryId] = useState<number | undefined>();
    const [gallery, setGallery] = useState<GalleryObject | null>(null);

    useEffect(() => {
        let isMounted = true;
        const load = async () => {
            try {
                const activation = await getActivation(
                    activationId ? +activationId : 0,
                );

                const photos = await getSessionPhotos(
                    activation.id,
                    photoSessionId ? photoSessionId : '',
                );

                const galleries = await getGalleriesForActivation(
                    activationId ? +activationId : 0,
                );

                setGalleryId(galleries[0]?.id);
                setGallery(galleries[0]);

                if (!showHidden) {
                    setPhotos(
                        photos.filter(
                            (photo: MediaInstance) => !photo.isHidden,
                        ),
                    );
                    return;
                }

                setPhotos(photos);
            } catch (e) {
                console.error(e);
            } finally {
                isMounted && setIsLoading(false);
            }
        };

        load();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showHidden, uploadCount]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <div>
            <main className=" height-minus-header">
                <div className="relative isolate">
                    <header className="pb-4 pt-6 sm:pb-6">
                        <div className="mx-auto flex max-w-10xl flex-wrap items-center gap-6 px-4 sm:flex-nowrap sm:px-6 lg:px-8">
                            <h1 className="text-base font-semibold text-foreground">
                                <div className="text-sm leading-6 text-muted-foreground">
                                    <span className="text-foreground">
                                        Manage
                                    </span>
                                </div>
                                <div className="mt-1 text-2xl font-bold tracking-tight text-foreground">
                                    Session Photos
                                </div>
                            </h1>
                            <div className="order-last flex w-full gap-x-8 text-sm font-semibold leading-6 sm:order-none sm:w-auto sm:border-l sm:border-border sm:pl-6 sm:leading-7"></div>
                            <div className="flex-1" />
                            <div className="flex items-center gap-2">
                                <Button
                                    onClick={() => {
                                        navigator.clipboard.writeText(
                                            `${window.location.origin}/session/${galleryId}/${photoSessionId}`,
                                        );

                                        toast.success(
                                            'Link copied to clipboard',
                                        );
                                    }}
                                >
                                    Copy Link to Session
                                </Button>
                                <Button
                                    onClick={() => {
                                        setIsSending(true);
                                        sendUnsentSharesForSession(
                                            photoSessionId ?? '',
                                        );
                                        toast.success(
                                            'Shares sent successfully',
                                        );
                                        setIsSending(false);
                                    }}
                                >
                                    {isSending
                                        ? 'Sending...'
                                        : 'Send to Clients'}
                                </Button>

                                <Button
                                    onClick={() => {
                                        setIsShowingAddShare(true);
                                    }}
                                >
                                    Add New Client
                                </Button>
                            </div>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setIsShowingBulkUpload(true)}
                            >
                                <UploadIcon className="size-4" />
                            </Button>
                        </div>
                    </header>

                    <div className="flex items-center gap-4 pl-8 py-2">
                        <span className="text-sm text-muted-foreground">
                            Photo count: {photos.length}
                        </span>

                        <span className="flex items-center gap-2">
                            <Switch
                                id="show-hidden-create"
                                checked={showHidden}
                                onCheckedChange={(checked) =>
                                    setShowHidden(checked)
                                }
                            />
                            <Label htmlFor="show-hidden-create">Include Hidden</Label>
                        </span>
                    </div>
                    <PhotoManagerList
                        photos={photos}
                        setSelectedShareIdentifiers={
                            setSelectedShareIdentifiers
                        }
                        selectedShareIdentifiers={selectedShareIdentifiers}
                        columnCount={4}
                        baseUrl={gallery?.baseUrl ?? null}
                        slug={gallery?.slug ?? null}
                        activationDevices={[]}
                    />

                    {isShowingBulkUpload ? (
                        <BulkUploadMedia
                            onCancel={() => setIsShowingBulkUpload(false)}
                            onComplete={() => {
                                setIsShowingBulkUpload(false);
                                alert('upload complete');
                                setUploadCount(uploadCount + 1);
                            }}
                            activationId={activationId ? +activationId : 0}
                            photoSessionId={
                                photoSessionId ? photoSessionId : ''
                            }
                        />
                    ) : null}

                    {isShowingAddShare ? (
                        <AddSessionShareModal
                            photoSessionId={photoSessionId ?? ''}
                            shareIdentifier={photoSessionId ?? ''}
                            activationId={activationId ? +activationId : 0}
                            galleryId={galleryId ? +galleryId : 0}
                            onComplete={() => setIsShowingAddShare(false)}
                        />
                    ) : null}
                </div>
            </main>
        </div>
    );
}
